import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Send, Users, MessageCircle, ArrowLeft, Plus, Bomb, CheckCheck, Image, Search, X, Loader2, Shield
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { useLeagueChat, LeagueMessage, DirectMessageRow } from '@/hooks/useLeagueChat';
import {
  mockLeagueMessages, mockConversations, mockDirectMessages, leagueMembers as mockMembers
} from '@/data/chatData';

// Giphy API
const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || '';
const GIPHY_SEARCH_URL = 'https://api.giphy.com/v1/gifs/search';
const GIPHY_TRENDING_URL = 'https://api.giphy.com/v1/gifs/trending';

interface GiphyGif {
  id: string;
  title: string;
  images: {
    fixed_width_small: { url: string; width: string; height: string };
    fixed_width: { url: string; width: string; height: string };
  };
}

const isGifMessage = (msg: string) =>
  /^https?:\/\/.*\.giphy\.com\/.*\.(gif|webp)/i.test(msg.trim());

interface ChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  embedded?: boolean;
}

const ChatModal = ({ open, onOpenChange, embedded }: ChatModalProps) => {
  const chat = useLeagueChat();
  const useMock = !chat.hasLeague;

  const [activeTab, setActiveTab] = useState<'league' | 'dms'>('league');
  const [activePartnerId, setActivePartnerId] = useState<string | null>(null);
  const [showNewDm, setShowNewDm] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const scrollEndRef = useRef<HTMLDivElement>(null);

  // GIF picker state
  const [gifOpen, setGifOpen] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [gifLoading, setGifLoading] = useState(false);
  const gifDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Fetch trending GIFs when picker opens
  useEffect(() => {
    if (!gifOpen) return;
    if (gifSearch.trim()) return;
    setGifLoading(true);
    fetch(`${GIPHY_TRENDING_URL}?api_key=${GIPHY_API_KEY}&limit=20&rating=pg-13`)
      .then(r => r.json())
      .then(data => setGifs(data.data || []))
      .catch(() => setGifs([]))
      .finally(() => setGifLoading(false));
  }, [gifOpen]);

  // Debounced GIF search
  useEffect(() => {
    if (!gifOpen) return;
    if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current);
    if (!gifSearch.trim()) {
      setGifLoading(true);
      fetch(`${GIPHY_TRENDING_URL}?api_key=${GIPHY_API_KEY}&limit=20&rating=pg-13`)
        .then(r => r.json())
        .then(data => setGifs(data.data || []))
        .catch(() => setGifs([]))
        .finally(() => setGifLoading(false));
      return;
    }
    gifDebounceRef.current = setTimeout(() => {
      setGifLoading(true);
      fetch(`${GIPHY_SEARCH_URL}?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(gifSearch)}&limit=20&rating=pg-13`)
        .then(r => r.json())
        .then(data => setGifs(data.data || []))
        .catch(() => setGifs([]))
        .finally(() => setGifLoading(false));
    }, 400);
    return () => { if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current); };
  }, [gifSearch, gifOpen]);

  const sendGifAsMessage = (gif: GiphyGif, sendFn: (msg: string) => void) => {
    const gifUrl = gif.images.fixed_width.url;
    sendFn(gifUrl);
    setGifOpen(false);
    setGifSearch('');
    setGifs([]);
  };

  // Mock state fallbacks
  const [mockLeagueMsgs, setMockLeagueMsgs] = useState(mockLeagueMessages);
  const [mockConvs, setMockConvs] = useState(mockConversations);
  const [mockDms, setMockDms] = useState(mockDirectMessages);

  useEffect(() => {
    // Delay to ensure DOM has rendered the messages before scrolling (longer for mobile)
    const t = setTimeout(() => {
      scrollEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }, 150);
    return () => clearTimeout(t);
  }, [chat.leagueMessages, chat.activeDmMessages, mockLeagueMsgs, activePartnerId, open]);

  // Load DM thread when partner changes
  useEffect(() => {
    if (activePartnerId && !useMock) {
      chat.loadDmThread(activePartnerId);
    }
  }, [activePartnerId, useMock]);

  const handleSendLeague = () => {
    if (!newMessage.trim()) return;
    if (useMock) {
      setMockLeagueMsgs(prev => [...prev, {
        id: `lm-${Date.now()}`, senderId: 't1', senderName: 'You',
        content: newMessage, timestamp: new Date(),
      }]);
    } else {
      chat.sendLeagueMessage(newMessage);
    }
    setNewMessage('');
  };

  const handleSendDM = () => {
    if (!newMessage.trim() || !activePartnerId) return;
    if (useMock) {
      const convId = mockConvs.find(c => c.participantId === activePartnerId)?.id;
      if (convId) {
        setMockDms(prev => ({
          ...prev,
          [convId]: [...(prev[convId] || []), {
            id: `dm-${Date.now()}`, senderId: 't1', senderName: 'You',
            recipientId: activePartnerId, recipientName: '',
            content: newMessage, timestamp: new Date(),
          }],
        }));
      }
    } else {
      chat.sendDirectMessage(activePartnerId, newMessage);
    }
    setNewMessage('');
  };

  const handleStartNewDM = (memberId: string) => {
    setActivePartnerId(memberId);
    setShowNewDm(false);
  };

  const totalUnread = useMock
    ? mockConvs.reduce((s, c) => s + c.unreadCount, 0)
    : chat.totalUnread;

  const members = useMock
    ? mockMembers.filter(m => m.id !== 't1')
    : chat.leagueMembers;

  const renderMessage = (msg: { id: string; content: string; is_system?: boolean; isSystem?: boolean; is_admin?: boolean; sender_name?: string; senderName?: string; senderId?: string; sender_id?: string; created_at?: string; timestamp?: Date }, isOwn: boolean) => {
    const isSystem = msg.is_system || msg.isSystem;
    const isAdmin = !!(msg as any).is_admin;
    const senderName = msg.sender_name || msg.senderName || '';
    const time = msg.created_at ? new Date(msg.created_at) : msg.timestamp || new Date();

    return (
      <div key={msg.id} className={cn("flex gap-2 mb-3", isOwn && !isAdmin && "flex-row-reverse", isSystem && "justify-center")}>
        {isSystem ? (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm">
            <Bomb className="w-3 h-3" />
            {msg.content}
          </div>
        ) : isAdmin ? (
          <div className="w-full flex gap-2 mb-1">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 bg-orange-500 text-white">
              <Shield className="w-4 h-4" />
            </div>
            <div className="max-w-[80%] rounded-2xl px-4 py-2 bg-orange-500/15 border border-orange-500/30 rounded-bl-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <p className="text-xs font-bold text-orange-400">{senderName}</p>
                <Badge className="bg-orange-500/20 text-orange-400 text-[9px] h-4 px-1.5">ADMIN</Badge>
              </div>
              {isGifMessage(msg.content) ? (
                <img src={msg.content.trim()} alt="GIF" className="rounded max-w-[200px] max-h-[150px]" loading="lazy" />
              ) : (
                <p className="text-sm">{msg.content}</p>
              )}
              <p className="text-[10px] mt-1 text-muted-foreground">
                {formatDistanceToNow(time, { addSuffix: true })}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
              isOwn ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            )}>
              {senderName.charAt(0)}
            </div>
            <div className={cn(
              "max-w-[70%] rounded-2xl px-4 py-2",
              isOwn ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"
            )}>
              {!isOwn && <p className="text-xs font-medium mb-1 opacity-70">{senderName}</p>}
              {isGifMessage(msg.content) ? (
                <img src={msg.content.trim()} alt="GIF" className="rounded max-w-[200px] max-h-[150px]" loading="lazy" />
              ) : (
                <p className="text-sm">{msg.content}</p>
              )}
              <p className={cn("text-[10px] mt-1", isOwn ? "text-primary-foreground/70" : "text-muted-foreground")}>
                {formatDistanceToNow(time, { addSuffix: true })}
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  // Build conversation list for display
  const conversationList = useMock
    ? mockConvs.map(c => ({
        partnerId: c.participantId,
        partnerName: c.participantName,
        lastMessage: c.lastMessage,
        lastTime: c.lastMessageTime,
        unread: c.unreadCount,
      }))
    : chat.dmConversations.map(c => ({
        partnerId: c.recipientId,
        partnerName: c.recipientName,
        lastMessage: c.lastMessage,
        lastTime: new Date(c.lastMessageTime),
        unread: c.unreadCount,
      }));

  // Messages for active DM thread
  const activeDmMsgs = useMock
    ? (mockDms[mockConvs.find(c => c.participantId === activePartnerId)?.id || ''] || [])
    : chat.activeDmMessages;

  const isOwnMessage = (msg: any) => {
    if (useMock) return msg.senderId === 't1';
    return msg.user_id === chat.userId || msg.sender_id === chat.userId;
  };

  const activePartnerName = useMock
    ? mockMembers.find(m => m.id === activePartnerId)?.name
    : chat.leagueMembers.find(m => m.id === activePartnerId)?.name
      || chat.dmConversations.find(c => c.recipientId === activePartnerId)?.recipientName;

  const chatContent = (
        <Tabs
          value={activeTab}
          onValueChange={(v) => { setActiveTab(v as 'league' | 'dms'); setActivePartnerId(null); setShowNewDm(false); }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="px-4 pt-2">
            <TabsList className="w-full">
              <TabsTrigger value="league" className="flex-1 gap-2"><Users className="w-4 h-4" />League</TabsTrigger>
              <TabsTrigger value="dms" className="flex-1 gap-2">
                <MessageCircle className="w-4 h-4" />DMs
                {totalUnread > 0 && (
                  <Badge variant="destructive" className="h-5 w-5 p-0 flex items-center justify-center text-[10px]">{totalUnread}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* League Chat */}
          <TabsContent value="league" className="flex-1 flex flex-col mt-0 overflow-hidden">
            <ScrollArea className="flex-1 px-4">
              <div className="py-4">
                {(useMock ? mockLeagueMsgs : chat.leagueMessages).map(msg => renderMessage(msg, isOwnMessage(msg)))}
                {!useMock && chat.leagueMessages.length === 0 && !chat.loading && (
                  <div className="text-center text-muted-foreground py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-sm">Be the first to say something!</p>
                  </div>
                )}
                <div ref={scrollEndRef} />
              </div>
            </ScrollArea>
            {/* GIF Picker */}
            {gifOpen && (
              <div className="border-t border-border bg-background/95 backdrop-blur-sm">
                <div className="flex items-center gap-1.5 px-3 pt-2">
                  <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                  <Input value={gifSearch} onChange={(e) => setGifSearch(e.target.value)} placeholder="Search GIFs..." className="h-8 text-sm" autoFocus />
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={() => { setGifOpen(false); setGifSearch(''); setGifs([]); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="h-[180px] overflow-y-auto px-3 py-2">
                  {gifLoading ? (
                    <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                  ) : gifs.length === 0 ? (
                    <p className="text-center text-muted-foreground text-sm py-8">{gifSearch.trim() ? 'No GIFs found' : 'Loading...'}</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5">
                      {gifs.map((gif) => (
                        <button key={gif.id} onClick={() => sendGifAsMessage(gif, useMock ? (m) => setMockLeagueMsgs(prev => [...prev, { id: `lm-${Date.now()}`, senderId: 't1', senderName: 'You', content: m, timestamp: new Date() }]) : chat.sendLeagueMessage)} className="rounded overflow-hidden hover:ring-2 ring-primary transition-all cursor-pointer">
                          <img src={gif.images.fixed_width_small.url} alt={gif.title} className="w-full h-auto" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="px-3 pb-1.5">
                  <img src="https://media.giphy.com/attribution/powered-by-giphy-dark.png" alt="Powered by GIPHY" className="h-3 opacity-50" />
                </div>
              </div>
            )}
            <div className="p-4 border-t">
              <form onSubmit={(e) => { e.preventDefault(); handleSendLeague(); }} className="flex gap-2">
                <Button type="button" size="icon" variant={gifOpen ? 'secondary' : 'ghost'} className={cn("shrink-0", gifOpen && "bg-primary/20")} onClick={() => setGifOpen(!gifOpen)}>
                  <Image className="w-4 h-4" />
                </Button>
                <Input placeholder="Message the league..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} className="flex-1" />
                <Button type="submit" size="icon" disabled={!newMessage.trim()}><Send className="w-4 h-4" /></Button>
              </form>
            </div>
          </TabsContent>

          {/* DMs */}
          <TabsContent value="dms" className="flex-1 flex flex-col mt-0 overflow-hidden">
            {!activePartnerId && !showNewDm ? (
              <>
                <div className="px-4 py-2 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowNewDm(true)}>
                    <Plus className="w-4 h-4 mr-2" />New Message
                  </Button>
                  {totalUnread > 0 && (
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                      <CheckCheck className="w-4 h-4 mr-2" />Mark All Read
                    </Button>
                  )}
                </div>
                <ScrollArea className="flex-1 px-4">
                  <div className="space-y-1 pb-4">
                    {conversationList.map(conv => (
                      <button key={conv.partnerId} onClick={() => { setActivePartnerId(conv.partnerId); }}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-left">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-bold">{conv.partnerName.charAt(0)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{conv.partnerName}</p>
                            <span className="text-xs text-muted-foreground">{formatDistanceToNow(conv.lastTime, { addSuffix: true })}</span>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{conv.lastMessage || 'No messages yet'}</p>
                        </div>
                        {conv.unread > 0 && (
                          <Badge variant="destructive" className="h-5 w-5 p-0 flex items-center justify-center text-[10px]">{conv.unread}</Badge>
                        )}
                      </button>
                    ))}
                    {conversationList.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        <p className="text-sm">No conversations yet</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            ) : showNewDm ? (
              <>
                <div className="px-4 py-2 flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setShowNewDm(false)}><ArrowLeft className="w-4 h-4" /></Button>
                  <span className="font-medium">New Message</span>
                </div>
                <Separator />
                <ScrollArea className="flex-1 px-4">
                  <div className="space-y-1 py-4">
                    {members.map(member => (
                      <button key={member.id} onClick={() => handleStartNewDM(member.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-left">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-bold">{member.name.charAt(0)}</div>
                        <span className="font-medium">{member.name}</span>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </>
            ) : (
              <>
                <div className="px-4 py-2 flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setActivePartnerId(null)}><ArrowLeft className="w-4 h-4" /></Button>
                  <span className="font-medium">{activePartnerName}</span>
                </div>
                <Separator />
                <ScrollArea className="flex-1 px-4">
                  <div className="py-4">
                    {activeDmMsgs.length === 0 ? (
                      <div className="text-center text-muted-foreground py-8">
                        <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No messages yet</p>
                        <p className="text-sm">Start the conversation!</p>
                      </div>
                    ) : (
                      activeDmMsgs.map(msg => renderMessage(msg, isOwnMessage(msg)))
                    )}
                    <div ref={scrollEndRef} />
                  </div>
                </ScrollArea>
                {/* GIF Picker for DMs */}
                {gifOpen && (
                  <div className="border-t border-border bg-background/95 backdrop-blur-sm">
                    <div className="flex items-center gap-1.5 px-3 pt-2">
                      <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                      <Input value={gifSearch} onChange={(e) => setGifSearch(e.target.value)} placeholder="Search GIFs..." className="h-8 text-sm" autoFocus />
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={() => { setGifOpen(false); setGifSearch(''); setGifs([]); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="h-[180px] overflow-y-auto px-3 py-2">
                      {gifLoading ? (
                        <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                      ) : gifs.length === 0 ? (
                        <p className="text-center text-muted-foreground text-sm py-8">{gifSearch.trim() ? 'No GIFs found' : 'Loading...'}</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-1.5">
                          {gifs.map((gif) => (
                            <button key={gif.id} onClick={() => { if (activePartnerId) sendGifAsMessage(gif, (m) => useMock ? (() => {})() : chat.sendDirectMessage(activePartnerId, m)); }} className="rounded overflow-hidden hover:ring-2 ring-primary transition-all cursor-pointer">
                              <img src={gif.images.fixed_width_small.url} alt={gif.title} className="w-full h-auto" loading="lazy" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="px-3 pb-1.5">
                      <img src="https://media.giphy.com/attribution/powered-by-giphy-dark.png" alt="Powered by GIPHY" className="h-3 opacity-50" />
                    </div>
                  </div>
                )}
                <div className="p-4 border-t">
                  <form onSubmit={(e) => { e.preventDefault(); handleSendDM(); }} className="flex gap-2">
                    <Button type="button" size="icon" variant={gifOpen ? 'secondary' : 'ghost'} className={cn("shrink-0", gifOpen && "bg-primary/20")} onClick={() => setGifOpen(!gifOpen)}>
                      <Image className="w-4 h-4" />
                    </Button>
                    <Input placeholder="Send a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} className="flex-1" />
                    <Button type="submit" size="icon" disabled={!newMessage.trim()}><Send className="w-4 h-4" /></Button>
                  </form>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
  );

  if (embedded) {
    return <div className="flex flex-col h-[500px]">{chatContent}</div>;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-[600px] flex flex-col p-0 gap-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            League Chat
          </DialogTitle>
        </DialogHeader>
        {chatContent}
      </DialogContent>
    </Dialog>
  );
};

export default ChatModal;
